import AddIcon from "@mui/icons-material/Add";
import { Alert, Box, Button, Container, Dialog, Fab, Tooltip } from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SearchBoxPersisted from "components/SearchBoxPersisted";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useListTitle from "hooks/useListTitle";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  IRegexValidation,
  ListRequestOfRegexValidationFilter,
  RegexValidationFilter,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import RegexValidationCredentialsDialog from "./RegexValidationCredentialsDialog";
import RegexValidationFilters from "./RegexValidationFilters";
import RegexValidationCreateForm from "./RegexValidationCreateForm";
import RegexValidationUpdateForm from "./RegexValidationUpdateForm";

// LazyTable hands a node cell the whole row, not the accessor's value.
const RegexCell = ({ value }: { value: IRegexValidation }) => (
  <Box component="span" fontFamily="monospace">
    {value.value}
  </Box>
);

type CredentialCountCellProps = {
  value: IRegexValidation;
  onShowCredentials: (regexValidation: IRegexValidation) => void;
};

// An em dash when nothing uses the rule; otherwise the number, which opens the list it stands for.
// The tooltip sits on the number rather than the column header, which is where someone looking at a
// bare count actually hovers. The click is stopped so it does not also open the row for editing.
const CredentialCountCell = ({
  value,
  onShowCredentials,
}: CredentialCountCellProps) => {
  const { formatMessage: t } = useIntl();
  const count = value.credentialCount ?? 0;
  const explanation = t({ id: "regexValidationCredentialsColumnTooltip" });

  if (count === 0) {
    return (
      <Tooltip title={explanation}>
        <span>&mdash;</span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={explanation}>
      <Button
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onShowCredentials(value);
        }}
      >
        {count}
      </Button>
    </Tooltip>
  );
};

const RegexValidations = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { licenseService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  // Matches the column's accessor: the table compares the two to decide which header carries the
  // sort arrow, and sorting itself is case-insensitive on the service side either way.
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [editing, setEditing] = useState<IRegexValidation | null>(null);
  const [
    showingCredentials,
    setShowingCredentials,
  ] = useState<IRegexValidation | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const {
    regexValidationSearchTerm,
    regexValidationFilterLocationIds,
    regexValidationFilterProfessionIds,
    regexValidationFilterCredentialIds,
  } = persistanceStore.useState();

  useListTitle("regexValidationsTitle");

  const setSearchTerm = useCallback((term: string) => {
    persistanceStore.update((s) => {
      s.regexValidationSearchTerm = term;
    });
  }, []);

  // Search and the three filters combine, so any of them landing on a later page would show an empty
  // table until the user pages back.
  useEffect(() => {
    setPage(0);
  }, [
    regexValidationSearchTerm,
    regexValidationFilterLocationIds,
    regexValidationFilterProfessionIds,
    regexValidationFilterCredentialIds,
  ]);

  const listRequest = ListRequestOfRegexValidationFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: RegexValidationFilter.fromJS({
      searchText: regexValidationSearchTerm || undefined,
      locationIds: regexValidationFilterLocationIds,
      professionIds: regexValidationFilterProfessionIds,
      credentialIds: regexValidationFilterCredentialIds,
    }),
  });

  const { isError, data, error, isFetching } = useQuery(
    ["licenseService.getRegexValidations", listRequest],
    () => licenseService.getRegexValidations(listRequest),
    { keepPreviousData: true }
  );

  if (isError) {
    // Not the raw error object: this page is reachable by an operator, and a serialised server
    // error hands over stack traces and request metadata for nothing.
    return <Alert severity="error">{translateError(error, t)}</Alert>;
  }

  const onCreated = () => {
    queryClient.invalidateQueries("licenseService.getRegexValidations");
    createOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "regexValidationCreated" }))
    );
  };

  const onUpdated = () => {
    queryClient.invalidateQueries("licenseService.getRegexValidations");
    setEditing(null);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "regexValidationUpdated" }))
    );
  };

  const rows = ((data?.data as IRegexValidation[]) ?? []).map((r) => ({
    ...r,
    id: r.id!,
  }));

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      type: "text",
      label: t({ id: "regexValidationNameLabel" }),
    },
    {
      accessor: "value",
      type: "node",
      View: RegexCell,
      noSort: true,
      label: t({ id: "regexValidationValueLabel" }),
    },
    {
      accessor: "message",
      type: "text",
      noSort: true,
      label: t({ id: "regexValidationMessageLabel" }),
    },
    {
      accessor: "credentialCount",
      type: "node",
      align: "center",
      View: (props) => (
        <CredentialCountCell
          {...props}
          onShowCredentials={setShowingCredentials}
        />
      ),
      noSort: true,
      label: t({ id: "regexValidationCredentialsColumnLabel" }),
    },
  ];

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label={t({ id: "regexValidationCreateButton" })}
          color="primary"
          onClick={createOpenActions.setTrue}
        >
          <AddIcon />
        </Fab>
      </Box>

      <SearchBoxPersisted
        persistedSearchTerm={regexValidationSearchTerm}
        setPersistedSearchTerm={setSearchTerm}
      />

      <RegexValidationFilters />

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          loading: isFetching,
          title: t({ id: "regexValidationsTitle" }),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick: (_id: string, row: IRegexValidation) => setEditing(row),
        }}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <RegexValidationCreateForm
          onCreated={onCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>

      <Dialog open={!!editing} disableEnforceFocus maxWidth="sm" fullWidth>
        {editing && (
          <RegexValidationUpdateForm
            regexValidation={editing}
            onUpdated={onUpdated}
            onCancel={() => setEditing(null)}
          />
        )}
      </Dialog>

      <Dialog
        open={!!showingCredentials}
        disableEnforceFocus
        maxWidth="md"
        fullWidth
      >
        {showingCredentials && (
          <RegexValidationCredentialsDialog
            regexValidation={showingCredentials}
            onClose={() => setShowingCredentials(null)}
          />
        )}
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default RegexValidations;
